import type { PageType } from '@ramp106/omrjs-tracking'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { START_LOCATION } from 'vue-router'
import type { RouteLocationNormalized, Router } from 'vue-router'

import { ReviewsRouteName } from '@/router/types'
import { ph } from '@/services/posthog'

declare module '@ramp106/omrjs-tracking' {
  interface PageTypeExtensions {
    reviews_tools: true
    reviews_tool_profile: true
    reviews_tool_resources: true
    reviews_tool_campaigns: true
    reviews_tool_top_products_ranking: true
    reviews_services: true
    reviews_service_profile: true
    reviews_service_resources: true
    reviews_service_reviews: true
    reviews_leads: true
    reviews_lead_detail: true
    manage_dashboard: true
    manage_users: true
    manage_notifications: true
  }
}

const ROUTE_PAGE_TYPES: Record<string, PageType> = {
  [ReviewsRouteName.ReviewsProducts]: 'reviews_tools',
  [ReviewsRouteName.ReviewsProductsIndex]: 'reviews_tools',
  [ReviewsRouteName.ReviewsProductLeads]: 'reviews_leads',
  [ReviewsRouteName.ReviewsProductLeadDetail]: 'reviews_lead_detail',
  [ReviewsRouteName.ReviewsProductTopProductsRanking]: 'reviews_tool_top_products_ranking',
  [ReviewsRouteName.ReviewsAgencies]: 'reviews_services',
  [ReviewsRouteName.ReviewsAgenciesIndex]: 'reviews_services',
  [ReviewsRouteName.ReviewsAgencyResourcesView]: 'reviews_service_resources',
  [ReviewsRouteName.ReviewsAgencyResourcesWidgetsView]: 'reviews_service_resources',
  [ReviewsRouteName.ReviewsAgencyResourcesBadgesView]: 'reviews_service_resources',
  [ReviewsRouteName.ReviewsAgencyReviewsView]: 'reviews_service_reviews',
  [ReviewsRouteName.ReviewsAgencyReviewDetails]: 'reviews_service_reviews',
  [ReviewsRouteName.ReviewsAgencyLeadsView]: 'reviews_leads',
  [ReviewsRouteName.ReviewsAgencyLeadDetail]: 'reviews_lead_detail',
  dashboard: 'manage_dashboard',
  userList: 'manage_users',
  notifications: 'manage_notifications',
}

const PRODUCT_PREFIX = 'reviewsproduct'
const AGENCY_PREFIX = 'reviewsagency'

// Reviews-section routes plus the shared manage entry points (dashboard, user
// management) that these users still land on; festival / other general manage
// routes are intentionally untracked.
export function resolveReviewsPageType(routeName: string): PageType | undefined {
  const exact = ROUTE_PAGE_TYPES[routeName]
  if (exact) return exact

  const name = routeName.toLowerCase()
  if (name.startsWith(PRODUCT_PREFIX)) {
    const rest = name.slice(PRODUCT_PREFIX.length)
    if (rest.includes('resources')) return 'reviews_tool_resources'
    if (rest.includes('campaign') || rest.includes('review')) return 'reviews_tool_campaigns'
    return 'reviews_tool_profile'
  }
  if (name.startsWith(AGENCY_PREFIX)) return 'reviews_service_profile'

  return undefined
}

export function trackRoutePageView(to: RouteLocationNormalized): void {
  const routeName = typeof to.name === 'string' ? to.name : ''
  const pageType = resolveReviewsPageType(routeName)
  if (!pageType) return

  const toolId = typeof to.params.productId === 'string' ? to.params.productId : undefined
  const serviceId = typeof to.params.agencyId === 'string' ? to.params.agencyId : undefined

  track('page_view', {
    page_type: pageType,
    ...(toolId ? { tool_id: toolId } : {}),
    ...(serviceId ? { service_id: serviceId } : {}),
  })
}

export function installPageViewTracking(router: Router): void {
  router.afterEach((to, from, failure) => {
    if (failure) return
    if (from !== START_LOCATION && to.path === from.path) return
    ph.capturePageview(to.path)
    trackRoutePageView(to)
  })
}
